/**
 * Shared format / date helpers used across pages.
 *
 * Покриті vitest-тестами в `format.test.ts`. Усі сторінки повинні імпортувати
 * звідси, щоб не дублювати реалізацію.
 */

/** Безпечно нормалізує `number | string | null` → `number | null`. */
export function num(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isFinite(n) ? n : null;
}

/** Локалізоване форматування числа. `null` → «—». */
export function fmt(n: number | null, digits = 0): string {
  if (n === null) return "—";
  return n.toLocaleString("uk-UA", {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  });
}

/** YYYY-MM-DD сьогодні (UTC midnight локального дня). */
export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** YYYY-MM-DD через `daysAgo` днів назад від сьогодні. */
export function daysAgoIso(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().slice(0, 10);
}

/** Locale-незалежна локальна година Kyiv: «00».."24" (24 для DST autumn duplicate). */
export function hourFromTs(ts: string): number {
  const d = new Date(ts);
  const h = d.toLocaleString("uk-UA", {
    hour: "2-digit",
    timeZone: "Europe/Kyiv",
    hour12: false,
  });
  return Number(h.replace(/[^\d]/g, ""));
}

/** "HH:00" з 0..24. */
export function fmtHourLocal(hour: number): string {
  return `${String(hour).padStart(2, "0")}:00`;
}

export type Period = "week" | "month" | "quarter" | "year";

/** День-діапазон для PeriodSwitcher. `end` inclusive. */
export function rangeFor(end: string, period: Period): { from: string; to: string } {
  const to = new Date(end);
  const from = new Date(to);
  const offset = period === "week" ? 6 : period === "month" ? 29 : period === "quarter" ? 89 : 364;
  from.setDate(to.getDate() - offset);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

/** N-day range з кінцем `end` (inclusive). days=1 → [end..end]. */
export function rangeDaysBefore(end: string, days: number): { from: string; to: string } {
  const to = new Date(end);
  const from = new Date(to);
  from.setDate(to.getDate() - (days - 1));
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}
